import type { WorldDocument } from "../document/types.ts";
import { isNote } from "./protocol-stream.ts";
import { INTENT_KINDS, LIVE_OPS_VERSION, OP_INTENTS_MAX, cellsOf, opOf, type Intent } from "./ops.ts";
import { STROKE_CELLS_MAX } from "./resolve-cells.ts";
import { resolveOp } from "./resolve.ts";
import type { IntentOutcome } from "./resolve-types.ts";

/**
 * The body of the operations route (ADR-099 decisions 19 and 20, note 39): one group of intents, the revision the
 * caller planned it on, a note and, optionally, the id of the caller's plan, which the host records with the save, read and applied alike by every host of Daedalus. A program changes a world by
 * sending this, never a document. The bounds hold for every caller; the list of kinds holds for a restricted
 * caller (a host's service identity), and a person may send any intent.
 */

/**
 * The largest body a host reads, in bytes.
 */
export const OPERATIONS_BODY_MAX = 256 * 1024;

/**
 * The most cells one body's patches cover: the stroke bound, below the heavy gate.
 */
export const OPERATIONS_CELLS_MAX = STROKE_CELLS_MAX;

/**
 * The most prop or entity intents one body carries. While `OP_INTENTS_MAX` is 64 the intent bound is met first.
 */
export const OPERATIONS_ITEMS_MAX = 200;

/**
 * The kinds that place and describe a map on the world's grid: the grid, the map groups, a map's place, group,
 * draft mark, host, share and capacity. A restricted caller never sends them (ADR-149 decision 4). A map's atmosphere is
 * not one of them: the Maps tab sets it with no layout permission, so a restricted caller may send `setMapAtmosphere`
 * (P1.11-T97).
 */
export const MAP_LAYOUT_KINDS: readonly Intent["k"][] = ["placeMap", "setGrid", "setMapGroups", "setMapGroup", "setMapDraft", "setMapHost", "setMapShare", "setMapCapacity"];

/**
 * The kinds a restricted caller may send (ADR-149 decision 4): every intent kind but the map layout's. The seams
 * and their crossings are among them; a host still takes them only from an account holding its layout permission, which
 * is the capability gate and not this list.
 */
export const CLIENT_INTENT_KINDS: readonly Intent["k"][] = INTENT_KINDS.filter((kind) => !MAP_LAYOUT_KINDS.includes(kind));

const ITEM_KINDS: ReadonlySet<Intent["k"]> = new Set(["addProp", "updateProp", "removeProp", "addEntity", "updateEntity", "removeEntity"]);

/**
 * The longest revision a body names: a build server's sequence or a dev server's content hash.
 */
const BASE = /^[0-9A-Za-z]{1,64}$/;

/**
 * The longest plan id a body names.
 */
export const OPERATIONS_PLAN_MAX = 80;

/**
 * A plan id: lowercase letters, digits and `-`.
 */
const PLAN = new RegExp(`^[a-z0-9-]{1,${OPERATIONS_PLAN_MAX}}$`);

/**
 * A body as read.
 */
export interface OperationsBody {
  readonly v: number;
  /** The revision the caller planned on, as the host's `ETag` names it, unquoted. */
  readonly base: string;
  readonly note: string | null;
  /** The id of the plan the caller made the list from, or null when it names none. */
  readonly plan: string | null;
  readonly list: readonly Intent[];
}

/**
 * A body, or why it was refused: 400 `invalid` for what is not a body, 422 `version`, `limit` or `kind`.
 */
export type OperationsRead = { readonly ok: true; readonly body: OperationsBody } | { readonly ok: false; readonly status: 400 | 422; readonly error: "invalid" | "version" | "limit" | "kind"; readonly message: string };

/**
 * A refusal.
 *
 * @param status The status.
 * @param error The code.
 * @param message The sentence.
 * @returns The refusal.
 */
function refused(status: 400 | 422, error: "invalid" | "version" | "limit" | "kind", message: string): OperationsRead {
  return { ok: false, status, error, message };
}

/**
 * The map an intent names, or null for one that names none.
 *
 * @param intent The intent.
 * @returns The map's id.
 */
function mapOfIntent(intent: Intent): string | null {
  switch (intent.k) {
    case "setTerrainName":
    case "changeTilesets":
    case "setGrid":
    case "setMapGroups":
    case "setSeam":
    case "removeSeam":
    case "setCrossing":
    case "removeCrossing":
      return null;
    case "addMap":
      return intent.map.id;
    default:
      return intent.map;
  }
}

/**
 * Reads a body already parsed from JSON.
 *
 * @param value The value.
 * @param restricted Whether the caller may send only `CLIENT_INTENT_KINDS`.
 * @returns The body, or the refusal.
 */
export function readOperationsBody(value: unknown, restricted: boolean): OperationsRead {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return refused(400, "invalid", "the body is an object of v, base, note and list, and optionally plan");
  }
  const fields = value as Record<string, unknown>;
  const keys = Object.keys(fields)
    .filter((key) => key !== "plan")
    .sort();
  if (keys.join(",") !== "base,list,note,v") {
    return refused(400, "invalid", "the body is an object of exactly v, base, note and list, and optionally plan");
  }
  const { v, base, note, plan, list } = fields;
  if (typeof base !== "string" || !BASE.test(base)) {
    return refused(400, "invalid", "base is the revision the edits were planned on, as a string");
  }
  if (note !== null && !isNote(note)) {
    return refused(400, "invalid", "note is null or one line of at most 120 characters");
  }
  if (plan !== undefined && plan !== null && (typeof plan !== "string" || !PLAN.test(plan))) {
    return refused(400, "invalid", `plan is null or 1 to ${OPERATIONS_PLAN_MAX} lowercase letters, digits and -`);
  }
  if (!Array.isArray(list) || list.length === 0) {
    return refused(400, "invalid", "list is one group of at least one intent");
  }
  if (v !== LIVE_OPS_VERSION) {
    return refused(422, "version", `this host reads operations of version ${LIVE_OPS_VERSION}`);
  }
  if (list.length > OP_INTENTS_MAX) {
    return refused(422, "limit", `a body carries at most ${OP_INTENTS_MAX} intents`);
  }
  const op = opOf({ id: "body-1", group: "body-1", list });
  if (op === null) {
    return refused(400, "invalid", "list is not a group of intents as an operation carries them");
  }
  if (op.list.filter((intent) => ITEM_KINDS.has(intent.k)).length > OPERATIONS_ITEMS_MAX) {
    return refused(422, "limit", `a body carries at most ${OPERATIONS_ITEMS_MAX} prop or entity intents`);
  }
  if (cellsOf(op.list) > OPERATIONS_CELLS_MAX) {
    return refused(422, "limit", `a body's patches cover at most ${OPERATIONS_CELLS_MAX} cells`);
  }
  const maps = new Set(op.list.map(mapOfIntent).filter((map): map is string => map !== null));
  if (maps.size > 1) {
    return refused(422, "limit", "a body changes one map");
  }
  if (restricted) {
    const other = op.list.find((intent) => !CLIENT_INTENT_KINDS.includes(intent.k));
    if (other !== undefined) {
      return refused(422, "kind", `this caller may not send ${other.k}`);
    }
  }
  return { ok: true, body: { v: LIVE_OPS_VERSION, base, note, plan: typeof plan === "string" ? plan : null, list: op.list } };
}

/**
 * A group applied, or refused whole.
 */
export type OperationsApplied = { readonly ok: true; readonly document: WorldDocument; readonly outcomes: readonly IntentOutcome[]; readonly changed: boolean } | { readonly ok: false; readonly outcomes: readonly IntentOutcome[] };

/**
 * Applies a group to a document: `resolveOp`, refused whole when any intent is skipped.
 *
 * @param document The head's document; never mutated.
 * @param list The intents.
 * @returns The document and each intent's outcome, with whether anything changed; or the outcomes alone when an
 *   intent was skipped.
 */
export function applyOperations(document: WorldDocument, list: readonly Intent[]): OperationsApplied {
  const resolved = resolveOp(document, list);
  if (resolved.outcomes.some((outcome) => outcome.status === "skipped")) {
    return { ok: false, outcomes: resolved.outcomes };
  }
  return { ok: true, document: resolved.document, outcomes: resolved.outcomes, changed: resolved.outcomes.some((outcome) => outcome.status === "applied") };
}
